/**
 * One evaluation, as shown straight after it is made and again from the history.
 *
 * Read top to bottom at the counter: the call and how sure it is; one bar placing the
 * price against the maximum offer and the market value; the four figures; then why. The
 * AI's view and the rule check are both shown and labelled, any look ahead is marked as
 * an estimate, and the settings and rate used are at the bottom so a past result can be
 * understood later.
 */

import { useState } from "react";
import {
  Activity, AlertTriangle, CheckCircle2, ChevronDown, ExternalLink, Gauge, Minus, Sparkles, TrendingDown, TrendingUp,
} from "lucide-react";
import { CONDITION_LABEL, PLACE_LABEL } from "@ohc-price/shared";
import type { Evaluation } from "./api";
import { cardNumber, sgd, sgdCents, sgTime, shortDay, usd } from "./format";
import { Callout, Change, Chip, cx, VERDICT } from "./ui";

const UNCERTAINTY = {
  low: { label: "Low uncertainty", bars: 1, tone: "bg-emerald-500" },
  medium: { label: "Medium uncertainty", bars: 2, tone: "bg-amber-500" },
  high: { label: "High uncertainty", bars: 3, tone: "bg-rose-500" },
} as const;

export function EvaluationView({ e, showCard = false }: { e: Evaluation; showCard?: boolean }) {
  const [showSales, setShowSales] = useState(false);
  const f = e.figures;
  const rate = e.rate?.rate ?? null;
  const v = VERDICT[e.verdict];
  const u = UNCERTAINTY[e.uncertainty];
  const priceWord = e.priceKind === "asking" ? "Their asking price" : "Our offer";
  const trendIcon =
    e.trend.changePercent == null ? <Minus className="h-4 w-4" /> : e.trend.changePercent >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />;

  return (
    <article className="card overflow-hidden">
      {showCard && (
        <div className="border-b border-slate-100 px-4 py-3">
          <div className="font-semibold text-slate-900">
            {e.card.cardName}
            {e.card.variants.length > 0 && <span className="ml-1.5 text-sm font-medium text-gold-700">{e.card.variants.join(", ")}</span>}
          </div>
          <div className="num text-xs text-slate-500">{cardNumber(e.card.number, e.card.setTotal)} · {e.card.setName}</div>
        </div>
      )}

      {/* The call */}
      <div className={cx("flex items-center gap-4 px-4 py-4 ring-1 ring-inset", v.soft)}>
        <div className={cx("flex h-14 min-w-[92px] items-center justify-center rounded-2xl px-4 text-2xl font-bold tracking-tight shadow-sm", v.solid)}>
          {v.word}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-[13px] font-semibold">
            {e.verdictSource === "ai" ? <Sparkles className="h-4 w-4" /> : <Gauge className="h-4 w-4" />}
            {e.verdictSource === "ai" ? "AI assessment" : "Rule check"}
          </div>
          <div className="mt-1 flex items-center gap-2 text-xs">
            <span className="flex gap-0.5">
              {[1, 2, 3].map((i) => (
                <span key={i} className={cx("h-2.5 w-1.5 rounded-sm", i <= u.bars ? u.tone : "bg-black/10")} />
              ))}
            </span>
            {u.label}
          </div>
        </div>
      </div>

      {/* Where the price sits */}
      {f.valueSgd != null && f.maxOfferSgd != null && <PricePosition price={e.priceSgdCents} max={f.maxOfferSgd} value={f.valueSgd} verdictDot={v.dot} />}

      <div className="grid grid-cols-2 gap-px border-y border-slate-100 bg-slate-100">
        <Stat label={priceWord} value={sgdCents(e.priceSgdCents)} note={f.pricePercentOfValue != null ? `${f.pricePercentOfValue}% of market value` : undefined} />
        <Stat label="Market value" value={sgdCents(f.valueSgd)} note={f.lowSgd != null ? `${sgdCents(f.lowSgd)} to ${sgdCents(f.highSgd)}` : "no value from sales"} />
        <Stat label="Maximum offer" value={sgdCents(f.maxOfferSgd)} note={`${PLACE_LABEL[e.place ?? "show"]}: ${100 - f.purchaseDiscount}% of value`} accent />
        <Stat
          label="Difference"
          value={f.differenceSgd == null ? "-" : `${f.differenceSgd > 0 ? "+" : f.differenceSgd < 0 ? "−" : ""}${sgdCents(Math.abs(f.differenceSgd))}`}
          note={f.differenceSgd == null ? undefined : f.differenceSgd > 0 ? "above market value" : f.differenceSgd < 0 ? "under market value" : "at market value"}
        />
      </div>

      <div className="space-y-4 px-4 py-4">
        {e.grade === "ungraded" && e.condition && f.conditionDiscount > 0 && (
          <p className="text-xs text-slate-500">
            Raw, {CONDITION_LABEL[e.condition]}: {f.conditionDiscount}% taken off the near mint value, by OHC's setting.
          </p>
        )}

        {(e.rules.uncertaintyReasons.length > 0 || e.sales.readFailed) && (
          <div className="space-y-2">
            {e.sales.readFailed && <Callout kind="error">New sales could not be read: {e.sales.readFailed}</Callout>}
            {e.rules.uncertaintyReasons.map((r) => (
              <Callout key={r}>{r}</Callout>
            ))}
          </div>
        )}

        {e.ai ? (
          <div>
            {e.ai.summary && <p className="text-[15px] font-medium leading-snug text-slate-900">{e.ai.summary}</p>}
            <Bullets items={e.ai.reasons} icon={<CheckCircle2 className="h-4 w-4 text-navy-600" />} title="Why" />
            <Bullets items={e.ai.risks} icon={<AlertTriangle className="h-4 w-4 text-amber-600" />} title="Risks" />
            {e.ai.outlook && (
              <div className="mt-3 rounded-xl bg-navy-50 px-3 py-2.5 text-[13px] leading-snug text-navy-900">
                <span className="mr-1 font-semibold">Outlook.</span>
                {e.ai.outlook}
              </div>
            )}
            {e.ai.sources.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {e.ai.sources.map((s) => (
                  <a
                    key={s.url}
                    href={s.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex max-w-full items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-[11px] text-slate-600 hover:border-slate-300"
                  >
                    <ExternalLink className="h-3 w-3 flex-none" />
                    <span className="truncate">{s.title || s.url}</span>
                  </a>
                ))}
              </div>
            )}
            <div className="mt-4 flex items-start gap-2 rounded-xl bg-slate-50 px-3 py-2.5 text-[13px]">
              <Gauge className="mt-0.5 h-4 w-4 flex-none text-slate-400" />
              <div>
                <span className="font-semibold">Rule check: {VERDICT[e.rules.verdict].word}.</span>{" "}
                <span className="text-slate-600">{e.rules.reasons[0]}</span>
              </div>
            </div>
          </div>
        ) : (
          <div>
            <Bullets items={e.rules.reasons} icon={<CheckCircle2 className="h-4 w-4 text-navy-600" />} title="Why" />
            {e.aiError && <p className="mt-3 text-xs text-slate-500">No AI view: {e.aiError}</p>}
          </div>
        )}

        <div className="grid gap-2 sm:grid-cols-2">
          <Fact icon={trendIcon} title="Sold price trend" body={e.trend.sentence} />
          <Fact
            icon={<Activity className="h-4 w-4" />}
            title="How it sells"
            body={`${e.activity.last30}${e.activity.capped ? "+" : ""} sales in 30 days. ${e.activity.exitEstimate}`}
          />
        </div>

        <div>
          <button type="button" className="flex items-center gap-1 text-[13px] font-medium text-navy-700" onClick={() => setShowSales(!showSales)}>
            <ChevronDown className={cx("h-4 w-4 transition", showSales && "rotate-180")} />
            The {e.sales.used.length} sale{e.sales.used.length === 1 ? "" : "s"} used
            {e.sales.excluded.length ? `, ${e.sales.excluded.length} left out` : ""}
          </button>
          {showSales && (
            <ul className="mt-1 divide-y divide-slate-100 animate-fadein">
              {e.sales.used.map((s) => (
                <li key={s.saleId} className="flex gap-3 py-2 text-[13px]">
                  <span className="num w-12 flex-none text-xs text-slate-500">{shortDay(s.soldOn)}</span>
                  <span className="line-clamp-2 min-w-0 flex-1 text-slate-700">{s.title ?? "(no title)"}</span>
                  <span className="num flex-none text-right font-semibold">{sgd(s.priceCents, rate)}<span className="block text-[11px] font-normal text-slate-400">{usd(s.priceCents)}</span></span>
                </li>
              ))}
              {e.sales.excluded.map(({ sale: s, reason }) => (
                <li key={s.saleId} className="flex gap-3 py-2 text-[13px] opacity-70">
                  <span className="num w-12 flex-none text-xs text-slate-500">{shortDay(s.soldOn)}</span>
                  <span className="min-w-0 flex-1">
                    <span className="line-clamp-2 text-slate-600">{s.title ?? "(no title)"}</span>
                    <span className="text-[11px] font-medium text-amber-700">Left out: {reason}</span>
                  </span>
                  <span className="num flex-none text-right text-slate-500 line-through decoration-slate-300">{usd(s.priceCents)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <footer className="space-y-1 border-t border-slate-100 bg-slate-50/70 px-4 py-3 text-[11px] leading-relaxed text-slate-500">
        {e.valueNowSgdCents !== undefined && (
          <div className="flex items-center justify-between text-[13px] text-slate-700">
            <span>Market value now <span className="num font-semibold">{sgdCents(e.valueNowSgdCents)}</span></span>
            <Change percent={e.changePercent} />
          </div>
        )}
        <div>
          {e.by ? `${e.by} · ` : ""}
          {sgTime(e.createdAt)}
          {e.note && <> · <span className="text-slate-700">{e.note}</span></>}
        </div>
        <div className="flex flex-wrap gap-1">
          <Chip>Settings v{e.settingVersionId}</Chip>
          <Chip>{e.settings.comparables} sales in {e.settings.lookbackDays} days</Chip>
          <Chip>min {e.settings.minSample}</Chip>
          <Chip>{PLACE_LABEL[e.place ?? "show"]}, {e.place === "online" ? e.settings.purchaseDiscountOnline : e.settings.purchaseDiscountShow}% off</Chip>
          {e.rate && <Chip mono>US$1 = S${e.rate.rate}</Chip>}
          {e.ai && <Chip>{e.ai.model}</Chip>}
        </div>
      </footer>
    </article>
  );
}

/**
 * The price on one line with the two figures that decide it: under the maximum offer is
 * a buy, up to the market value is a wait, past it is a pass. Green, amber and red zones
 * make the call readable before any words.
 */
function PricePosition({ price, max, value, verdictDot }: { price: number; max: number; value: number; verdictDot: string }) {
  const top = Math.max(price, value) * 1.18;
  const pct = (n: number) => Math.min(Math.max((n / top) * 100, 1.5), 98.5);
  const at = (n: number) => `${pct(n)}%`;
  return (
    <div className="px-5 pb-4 pt-3">
      {/* The market value above the bar and the maximum offer below it, so close figures never overlap. */}
      <div className="relative h-9 text-[11px]">
        <Marker percent={pct(value)} label="Market value" value={sgdCents(value)} side="above" />
      </div>
      <div className="relative">
        <div className="relative h-3 overflow-hidden rounded-full">
          <div className="absolute inset-y-0 left-0 bg-emerald-200" style={{ width: at(max) }} />
          <div className="absolute inset-y-0 bg-amber-200" style={{ left: at(max), width: `${pct(value) - pct(max)}%` }} />
          <div className="absolute inset-y-0 right-0 bg-rose-200" style={{ left: at(value) }} />
        </div>
        <span
          className={cx("absolute top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full border-4 border-white shadow-md", verdictDot)}
          style={{ left: at(price) }}
          aria-label="The price"
        />
      </div>
      <div className="relative h-9 text-[11px]">
        <Marker percent={pct(max)} label="Max offer" value={sgdCents(max)} side="below" />
      </div>
    </div>
  );
}

/** A tick and its label, kept inside the card when it is near either end. */
function Marker({ percent, label, value, side }: { percent: number; label: string; value: string; side: "above" | "below" }) {
  const align = percent < 18 ? "translate-x-0 items-start" : percent > 82 ? "-translate-x-full items-end" : "-translate-x-1/2 items-center";
  const tick = <span className="h-1.5 w-px bg-slate-400" />;
  return (
    <div className={cx("absolute flex flex-col", align, side === "above" ? "bottom-0" : "top-0")} style={{ left: `${percent}%` }}>
      {side === "below" && tick}
      <span className="whitespace-nowrap">
        <span className="font-medium text-slate-500">{label} </span>
        <span className="num font-semibold text-slate-800">{value}</span>
      </span>
      {side === "above" && tick}
    </div>
  );
}

function Stat({ label, value, note, accent }: { label: string; value: string; note?: string | undefined; accent?: boolean }) {
  return (
    <div className="bg-white px-4 py-3">
      <div className="text-[11px] font-medium text-slate-500">{label}</div>
      <div className={cx("num mt-0.5 text-[19px] font-semibold tracking-tight", accent ? "text-gold-700" : "text-slate-900")}>{value}</div>
      {note && <div className="num mt-0.5 text-[11px] text-slate-500">{note}</div>}
    </div>
  );
}

function Bullets({ title, items, icon }: { title: string; items: string[]; icon: React.ReactNode }) {
  if (!items.length) return null;
  return (
    <div className="mt-3">
      <div className="label mb-1.5">{title}</div>
      <ul className="space-y-1.5">
        {items.map((item) => (
          <li key={item} className="flex gap-2 text-[13px] leading-snug text-slate-700">
            <span className="mt-0.5 flex-none">{icon}</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Fact({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <div className="flex gap-2.5 rounded-xl border border-slate-100 bg-white p-3">
      <span className="mt-0.5 flex h-7 w-7 flex-none items-center justify-center rounded-lg bg-slate-100 text-slate-600">{icon}</span>
      <div>
        <div className="text-[13px] font-semibold text-slate-800">{title}</div>
        <div className="mt-0.5 text-xs leading-snug text-slate-500">{body}</div>
      </div>
    </div>
  );
}
